<script setup lang="ts">
/**
 * Tells iOS Safari users how to put persolog Online on their home screen (RDT-296): open the share
 * sheet, then "Add to Home Screen". The share step is spelled out for both toolbars — current iOS
 * hides sharing behind the "•••" menu, older versions put the icon straight into the bar (and the
 * step says so, rather than sending those users hunting for a button they do not have).
 *
 * Android shows its own install prompt, so this is iOS-only; see
 * `useIosInstallHint` for when it appears at all. Closing it is final (persisted in localStorage).
 */
import { useI18n } from "vue-i18n";
import { useIosInstallHint } from "../../composables/useIosInstallHint.ts";

const { t } = useI18n();
const { visible, dismiss } = useIosInstallHint();
</script>

<template>
    <Message
        v-if="visible"
        severity="info"
        closable
        role="status"
        data-cy="ios-install-hint"
        :close-button-props="{ 'aria-label': t('accessibility.buttons.close') }"
        @close="dismiss"
    >
        <div class="hint">
            <strong>{{ t("installHint.ios.heading") }}</strong>
            <ol class="steps">
                <!-- No icon here: the step text already carries the "•••" glyph. -->
                <li>{{ t("installHint.ios.more") }}</li>
                <li>
                    {{ t("installHint.ios.share") }}
                    <i class="pi pi-upload" aria-hidden="true" />
                </li>
                <li>{{ t("installHint.ios.addToHomeScreen") }}</li>
            </ol>
        </div>
    </Message>
</template>

<style scoped>
.hint {
    text-align: left;
}

.steps {
    /* The steps are an order, so they need their numbers back: Tailwind's preflight resets them. */
    list-style: decimal;
    margin: 0.25rem 0 0;
    padding-left: 1.25rem;
}
</style>
